import { findIndex, forOwn } from 'lodash';
import moment from 'moment';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import update from 'immutability-helper';
import { logger } from 'utils/logger';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { AppConstants, CompanyFormatFields, CompanyFormats, DataFormats, PriorityTypes, PriorityTypesMap, priorityForDaysUntilOtb, ResponseCodes, StudioClientProdStatusTypes, colorThemes, status_unassigned, statuss } from './constants';
import { SampleStatusTypes } from 'core/models/SampleModel';
var _ = require('lodash');
/**
 * @file helper.js
 * Lib of helper components
 * @author Ben Yee
 */

export const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

export const matchesKeywords = (keyword, ...values) => {
    if (!keyword || !keyword.length) return true;
    const terms = keyword.toLowerCase().split(' ').filter(Boolean);
    if (!terms.length) return true;
    const combined = values.map((v) => (v ?? '').toString().toLowerCase());
    return terms.some((term) => combined.some((val) => val.includes(term)));
};

export const getSecondaryColor = (theme) => {
    const secondaryColor = theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[7];

    return secondaryColor;
};

export const createFrame = () => {
    return {
        id: null,
        label: '',
        items: [],
        width: 100,
        height: 100,
    };
};

export const cacheToken = (token) => {
    localStorage.setItem('oi-hgio_token', encode(token));
};

export const clearToken = () => {
    logger.log('trying to clear token');
    localStorage.removeItem('oi-hgio_token');
};

export const cacheProp = (prop, value) => {
    localStorage.setItem(prop, encode(value));
};

export const clearProp = (prop) => {
    localStorage.removeItem(prop);
};

export const getProp = (prop) => {
    return decode(localStorage.getItem(prop));
};

function decode(k) {
    if (!k) {
        return '';
    }
    return atob(k.replace(/-/g, ''));
}

function encode(s) {
    return btoa('' + s).replace(/(.{5})/g, '$1-');
}

export const getToken = () => {
    return decode(localStorage.getItem('oi-hgio_token'));
};

export const getUserInitialsFromName = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    const first = parts[0];
    const last = parts.length > 1 ? parts[parts.length - 1] : null;
    return last ? `${first.charAt(0).toUpperCase()}${last.charAt(0).toUpperCase()}` : first.charAt(0).toUpperCase();
};

export const getUserInitials = (first, last) => {
    return first && last ? `${first.charAt(0).toUpperCase()}${last.charAt(0).toUpperCase()}` : '';
};

export const getUserAvatarColour = (chars) => {
    if (chars.length < 2) {
        return 0;
    }

    const charCode1 = chars.charCodeAt(0) * 33;
    const charCode2 = chars.charCodeAt(1) * 7;

    const index = Math.floor((charCode1 + charCode2) % 12);

    return colorThemes[index + 1];
};

export const getUserFullname = (first, last) => {
    return `${first} ${last}`;
};

export const useEffectOnce = (effect) => {
    const destroyFunc = useRef();
    const effectCalled = useRef(false);
    const renderAfterCalled = useRef(false);
    const [val, setVal] = useState(0);

    if (effectCalled.current) {
        renderAfterCalled.current = true;
    }

    useEffect(() => {
        // only execute the effect first time around
        if (!effectCalled.current) {
            destroyFunc.current = effect();
            effectCalled.current = true;
        }

        // this forces one render after the effect is run
        setVal((val) => val + 1);

        return () => {
            // if the comp didn't render since the useEffect was called,
            // we know it's the dummy React cycle
            if (!renderAfterCalled.current) {
                return;
            }
            if (destroyFunc.current) {
                destroyFunc.current();
            }
        };
    }, []);
};

export const fetchImagePath = async (url) => {
    function readFileAsync(file) {
        return new Promise((resolve, reject) => {
            // let reader = new FileReader();

            // reader.onload = () => {
            //   resolve(reader.result);
            // };

            // reader.onerror = reject;

            // reader.readAsArrayBuffer(file);
            file.blob().then((result) => {
                resolve(URL.createObjectURL(result));
            });
            //const blob = res.blob()
            // const img = new Image()
            // // img.src = URL.createObjectURL(blob);
            // logger.log(img);
            // return img.src;
        });
    }

    let data = await fetch(url, {
        headers: {
            hgio: getToken(),
            // 'Content-Type': 'application/x-www-form-urlencoded'
        },
        method: 'GET',
        // body: formData,
    })
        .then((response) => {
            //return response.blob();

            return response;
            //return response.body;
        })
        .catch((err) => {
            logger.error('ERROR fetching presigned image', err);
            throw err;
        });

    return readFileAsync(data);

    // var reader = new FileReader();
    // reader.readAsDataURL(blob);
    // var data = await readFileAsync(blob);

    // logger.log(data);
    // return blob;
};

export const setObjectFromProps = (oldObject, newObject) => {
    var updatedObj = { ...oldObject };

    forOwn(newObject, function (value, key) {
        updatedObj[key] = value;
    });
    return updatedObj;
};

export const getCaptureCataglogues = () => {
    return [
        { label: 'Today', value: { since: moment().hour(0).minute(0).format('YYYY-MM-DD-HH-mm-ss-SSS'), until: moment().format('YYYY-MM-DD-HH-mm-ss-SSS') } },
        { label: 'This Week', value: { since: moment().subtract(1, 'weeks').startOf('week').format('YYYY-MM-DD-HH-mm-ss-SSS'), until: moment().format('YYYY-MM-DD-HH-mm-ss-SSS') } },
        { label: 'This Month', value: { since: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD-HH-mm-ss-SSS'), until: moment().format('YYYY-MM-DD-HH-mm-ss-SSS') } },
        { label: 'This Year', value: { since: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD-HH-mm-ss-SSS'), until: moment().format('YYYY-MM-DD-HH-mm-ss-SSS') } },
        {
            label: 'Last Week',
            value: { since: moment().subtract(2, 'weeks').startOf('week').format('YYYY-MM-DD-HH-mm-ss-SSS'), until: moment().subtract(1, 'weeks').startOf('week').format('YYYY-MM-DD-HH-mm-ss-SSS') },
        },
        {
            label: 'Last Month',
            value: {
                since: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD-HH-mm-ss-SSS'),
                until: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD-HH-mm-ss-SSS'),
            },
        },
        {
            label: 'Last Year',
            value: { since: moment().subtract(2, 'years').startOf('year').format('YYYY-MM-DD-HH-mm-ss-SSS'), until: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD-HH-mm-ss-SSS') },
        },
    ];
};

export const updateCaptureItems = (items, updatedItems) => {
    var tmpCaptures = [...items];

    const updateCapture = (val) => {
        logger.log('val: ', val);
        const idx = findCaptureIndex(tmpCaptures, val);

        if (idx > -1) {
            var newCapture = setObjectFromProps(tmpCaptures[idx], val);
            tmpCaptures = update(tmpCaptures, { [idx]: { $set: newCapture } });
            // arrCaptures = [...newState];
        }
    };

    logger.log(updatedItems);
    updatedItems.forEach((val, idx) => {
        updateCapture(val);
    });

    return tmpCaptures;
};

export const findCaptureIndex = (arr, val) => {
    let idx = -1;
    if (val) {
        idx = findIndex(arr, function (obj) {
            return obj.actionPart === val.actionPart && obj.actionSort === val.actionSort;
        });
    }
    return idx;
};

export const handleZipDownload = async (items, filenameKey, zipName, downloadProperty, extension = 'png', onProgress) => {
    var zip = new JSZip();

    function roundUpToTwoDecimals(num) {
        return Math.ceil(num * 100) / 100;
    }

    for (var i = 0; i < items.length; i++) {
        const item = items[i];
        const counter = i;
        const xhr = new XMLHttpRequest();
        await new Promise((resolve, reject) => {
            xhr.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = event.loaded / event.total;
                    if (onProgress) {
                        onProgress(roundUpToTwoDecimals(progress / items.length + (1 / items.length) * counter));
                    }
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    let fnPart = item[filenameKey].split('.');
                    const fn = `${fnPart[0]}.${extension}`;
                    zip.file(fn, xhr.response);
                    resolve(xhr.response);
                } else {
                    reject(new Error(`Failed to fetch file from ${item[downloadProperty]}. Status: ${xhr.status}`));
                }
            });
            xhr.open('GET', item[downloadProperty], true);
            // xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.responseType = 'blob';
            xhr.send();
        });
        // return success;
    }

    // wait for all download
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${zipName}_media.zip`);
};

export const downloadFile = async (url, filename, onProgress, onError, onComplete) => {
    logger.log(url, filename);

    // const arr = filename.split('.');
    // const extension = arr.length > 1 ? arr[1] : '';

    // let type = 'text/plain';
    let type = 'application/octet-stream';
    // switch (extension.toLowerCase()) {
    //     case 'eip':
    //     case 'psd': {
    //         type = 'application/octet-stream';
    //         break;
    //     }
    //     case 'jpeg':
    //     case 'jpg': {
    //         type = 'image/jpeg';
    //         break;
    //     }
    //     case 'png': {
    //         type = 'image/png';
    //         break;
    //     }
    //     case 'pdf': {
    //         type = 'application/pdf';
    //         break;
    //     }
    //     default: {
    //         type = 'text/plain';
    //         break;
    //     }
    // }

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';
    xhr.onprogress = (ev) => {
        const percent = (ev.loaded / ev.total) * 100;
        if (onProgress) onProgress(percent);
    };
    xhr.onload = function () {
        if (xhr.status === 200) {
            const arrayBuffer = xhr.response;

            let blob = new Blob([arrayBuffer], {
                type: type,
            });

            let tmpUrl = URL.createObjectURL(blob);

            logger.log(tmpUrl);

            // Create an anchor element
            const link = document.createElement('a');

            // Set the file URL as the anchor's href
            link.href = tmpUrl;

            // Set the download attribute with the desired filename
            link.download = filename;

            // Append the anchor element to the document
            document.body.appendChild(link);

            // Click the anchor to trigger the download
            link.click();

            // Remove the anchor element from the document
            document.body.removeChild(link);

            if (onComplete) onComplete();
        } else {
            // Handle errors here.
            logger.error('Failed to download PDF:', xhr.status, xhr.statusText);
            if (onError) onError();
        }
    };

    xhr.onerror = function () {
        // Handle network errors here.
        logger.error('Network error while downloading PDF.');
        if (onError) onError();
    };

    xhr.send();
};

export const isPureObject = (input) => {
    return null !== input && typeof input === 'object' && Object.getPrototypeOf(input).isPrototypeOf(Object);
};

export const requestCheck = (response) => {
    if (response && response.status !== 200) {
        throw new Error('Something went wrong');
    }
};

export const pluralize = (term, count) => {
    return count === 1 ? term : `${term}s`;
};

export const getFilterVars = (key, filterItems) => {
    var res = [];
    if (filterItems[key]) Object.keys(filterItems[key]).forEach((val) => res.push(val));
    return res;
};

export const getFilterMatch = (arr, item, itemKey) => {
    var itemMatch = false;
    for (let i = 0; i < arr.length; i++) {
        let tmpItem = arr[i];
        itemMatch = item[itemKey].indexOf(tmpItem) > -1 ? true : false;
        if (itemMatch) {
            itemMatch = true;
            break;
        }
    }
    return itemMatch;
};

function findParent(data, targetId) {
    let parent = null;

    function recurse(rows, maybeParent) {
        _.each(rows, function (row) {
            if (row.id === targetId) {
                parent = maybeParent;
                return;
            }
            if (row.subRows) {
                recurse(row.subRows, row);
            }
        });
    }

    recurse(data, null);
    return parent;
}

/**
 * Method to handle the selection of items in a group, used in the multicheckbox select views
 * @param {*} e the synthetic event
 * @param {*} item the current item
 * @param {*} selected whether the current item is selected or not
 * @param {*} page the filtered page items which is a subset of the data
 * @param {*} selectedItems state selected items
 * @param {*} items a copy of the selected
 */
export const selectItems = (e, item, selected, page, items, isCheckbox = true) => {
    if (selected) {
        if (e.nativeEvent.metaKey) {
            // if command held down we add the item to the array
            // if it's not yet selected.
            const obj = items.find((tmpItem) => tmpItem.id === item.id);
            // if the object is already selected then we unselect it
            if (obj) {
                //items  = [...selectedItems];
                items = _.remove([...items], (image) => {
                    return image.id !== item.id;
                });
            } else {
                items = [...items, { id: item.id }];
            }
        }
        // if they held the shift key select all the items from the last selected item
        else if (e.nativeEvent.shiftKey) {
            // if the data is grouped there is a subRow key for table data
            if (item.type === 'group') {
                var index = _.findIndex(page, (pageItem) => item.id === pageItem.id);
                var lastItem = findParent(page, items[items.length - 1].id);
                var lastIdx = lastItem ? _.findIndex(page, (pageItem) => lastItem.id === pageItem.id) : 0;

                var startIdx = index < lastIdx ? lastIdx : index;
                var endIdx = index < lastIdx ? index : lastIdx;
                for (let i = startIdx; i >= endIdx; i--) {
                    // if there's an index of the selected item then break;
                    var selectedIdx = _.findIndex(items, (item) => item?.id === page[i]?.id);

                    if (selectedIdx === -1) {
                        items.push(...page[i].subRows.map((tmpSub) => ({ id: tmpSub.id })));
                    }
                }
                items = _.uniqBy(items, 'id');
            } else {
                var index = _.findIndex(page, (pageItem) => item.id === pageItem.id);
                var lastItem = items[items.length - 1];
                var lastIdx = lastItem ? _.findIndex(page, (pageItem) => lastItem.id === pageItem.id) : 0;

                var startIdx = index < lastIdx ? lastIdx : index;
                var endIdx = index < lastIdx ? index : lastIdx;
                for (let i = startIdx; i >= endIdx; i--) {
                    // if there's an index of the selected item then break;
                    var selectedIdx = _.findIndex(items, (item) => item?.id === page[i]?.id);

                    if (selectedIdx === -1) {
                        items.push({ id: page[i].id });
                    }
                }
            }
        } else {
            var selectedIdx = _.findIndex(items, (tmpItem) => tmpItem.id === item.id);

            if (selectedIdx === -1) {
                // if it's a checkbox type selection then we just push the item
                // otherwise we treat it like a radio.
                if (isCheckbox) {
                    // items.push({ id: item.id });
                    // check the the data isn't grouped
                    if (item.type && item.type === 'group') {
                        items.push(...item.subRows.map((tmpSub) => ({ id: tmpSub.id })));
                        items = _.uniqBy(items, 'id');
                    } else {
                        items.push({ id: item.id });
                    }
                } else {
                    items.length = 0;
                    items.push({ id: item.id });
                }
            }
        }
    } else {
        if (item.type === 'group') {
            const subIds = new Set(item.subRows.map((s) => s.id));
            _.remove(items, (val) => subIds.has(val.id));
        } else {
            var tmpItem = items.find((val) => val.id === item.id);
            index = items.indexOf(tmpItem);
            if (index > -1) {
                items.splice(index, 1);
            }
        }
    }

    return items;
};

export function parseValue(item) {
    if (!item) return;

    const keys = Object.keys(item);
    var newItem = {};

    keys.forEach((key, idx) => {
        var val = item[key];
        const valueKey = Object.keys(val)[0];
        var newVal = val[valueKey];

        switch (valueKey) {
            // String type
            case 'S': {
                break;
            }
            // Number
            case 'N': {
                break;
            }
            // Object Type
            case 'M': {
                newVal = parseValue(val);
                break;
            }
            // Array type
            case 'L': {
                let items = val['L'];
                newVal = items.map((tmp) => {
                    if (tmp['M']) {
                        return parseValue(tmp['M']);
                    } else if (tmp['S']) {
                        return tmp['S'];
                    }
                });
                break;
            }
            default: {
                break;
            }
        }
        newItem[key] = newVal;
    });

    return newItem;
}

export function filterMedia(mediaItems, mediaFilter, tasks = []) {
    // grab all the media items for the given mediaFilter status.
    var items = [];
    var excludeList = [];
    var findDiff = false;
    if (mediaFilter.status && mediaFilter.status.taskStatus && mediaFilter.status.taskStatus.length) {
        mediaFilter.status.taskStatus.forEach((status) => {
            tasks.forEach((task) => {
                if (task.status === status) {
                    let captures = task.items.map((item) => item.actionSort);
                    if (captures) items.push(...captures);
                } else if (status === status_unassigned.value) {
                    let captures = task.items.map((item) => item.actionSort);
                    if (captures) excludeList.push(...captures);
                }
            });
            if (status === status_unassigned.value) findDiff = true;
        });

        // if we are including unassigned we add in all media items and remove the ones that are assigned.
        if (findDiff) {
            const allMedia = mediaItems.map((item) => item.actionSort);
            const diff = _.difference(allMedia, excludeList);
            items = [...items, ...diff];
        }
    }

   

    logger.log(mediaFilter, ' MEDIA FILTER');
    logger.log(mediaItems, ' ITEMS');

    let newData = mediaItems.filter((capture) => {
        var match = true;

        if (mediaFilter.sak.toString() && mediaFilter.sak.toString() !== '-1') {
            match = capture.sak.toString() === mediaFilter.sak.toString() ? true : false;
        }

        if (mediaFilter.star && mediaFilter.star > -1 && match) {
            match = parseInt(capture.star) === mediaFilter.star ? true : false;
        }

        if (mediaFilter.styles && mediaFilter.styles.length && match) {
            // loop over the tag and see if any match.
            var tagMatch = false;

            if (!capture.styles || (capture.styles && capture.styles.length === 0)) {
                tagMatch = false;
            } else {
                for (let i = 0; i < mediaFilter.styles.length; i++) {
                    let tag = mediaFilter.styles[i];

                    if (capture.styles.indexOf(tag) > -1) {
                        tagMatch = true;
                        break;
                    } else {
                        tagMatch = false;
                    }
                }
            }

            match = tagMatch;
        }

        if (mediaFilter.tags && mediaFilter.tags.length && match) {
            if (!capture.tags || (capture.tags && capture.tags.length === 0)) {
                tagMatch = false;
            } else {
                // loop over the tag and see if any match.
                tagMatch = true;
                for (let i = 0; i < mediaFilter.tags.length; i++) {
                    let tag = mediaFilter.tags[i].toLowerCase();
                    tagMatch = capture.tags.toLowerCase().indexOf(tag) > -1 ? true : false;
                    if (tagMatch) {
                        break;
                    }
                }
            }

            match = tagMatch;
        }

        if (mediaFilter.colourTag && mediaFilter.colourTag.length && match) {
            var colourTagMatch = false;
            
            // Check for empty string filter (items with no colour tags)
            if (mediaFilter.colourTag.indexOf('') > -1) {
                if(!capture.colourTag || capture.colourTag.length === 0){
                    colourTagMatch = true;
                }
            }
            
            // Check for specific colour tag matches (if not already matched by empty string)
            if (!colourTagMatch && capture.colourTag) {
                for (let i = 0; i < mediaFilter.colourTag.length; i++) {
                    let tag = mediaFilter.colourTag[i].toLowerCase();
                    // Skip empty string tags as they're handled above
                    if (tag !== '') {
                        if (capture.colourTag.toLowerCase().indexOf(tag) > -1) {
                            colourTagMatch = true;
                            break;
                        }
                    }
                }
            }

            match = colourTagMatch;
        }


        if (mediaFilter.status && mediaFilter.status.taskStatus && mediaFilter.status.taskStatus.length && match) {
            // find all tasks with the given status and check if the media has been assigned to those tasks.

            var statusMatch = false;

            if (items.indexOf(capture.actionSort) > -1) {
                statusMatch = true;
            }

            match = statusMatch;
        }

        if (mediaFilter.status && mediaFilter.status.taskCount && mediaFilter.status.taskCount.toString() && mediaFilter.status.taskCount.length && match) {
            var taskMatch = false;
            var captureTasks = [];
            // if the task count is 1 then we need to check the tasks items to match the capture.actionSort 
            if (mediaFilter.status.taskCount.toString() === '1') {
                tasks.forEach((task) => {           
                    let captures = task.items.map((item) => item.actionSort);
                    if (captures && captures.length) captureTasks.push(task);
                });

                if(captureTasks.length) {
                    taskMatch = true;
                }
            }else if(mediaFilter.status.taskCount.toString() === '0') {
                // Check if capture is NOT assigned to any task (unassigned)
                // Use some() to short-circuit early when a match is found
                const isAssigned = tasks.some((task) => 
                    task.items && task.items.some((item) => item.actionSort === capture.actionSort)
                );
                // If not assigned to any task, it matches the filter
                taskMatch = !isAssigned;
                logger.log(isAssigned ? 'Assigned' : 'Unassigned', ' CAPTURE TASK STATUS');
            }
            match = taskMatch;
            // tasks.forEach((task) => {           
            //    let captures = task.items.map((item) => item.actionSort);
            //    if (captures) items.push(...captures);
            // });
            logger.log(tasks, ' TASKS');
            logger.log(capture.actionSort, ' CAPTURE');
        }

        // filter by status.photographyCount & status.taskCount
        // if (mediaFilter.status.photographyCount && mediaFilter.status.photographyCount.length && match) {
        //     var photographyMatch = false;
        //     if (mediaFilter.status.photographyCount.toString() === '1' && capture.photographyCount && capture.photographyCount === 'Y') {
        //         photographyMatch = true;
        //     } else if (mediaFilter.status.photographyCount.toString() === '0' && !capture.photographyCount) {
        //         photographyMatch = true;
        //     }
        // }

        if (mediaFilter.psdLoaded.toString() && mediaFilter.psdLoaded.length && match) {
            var psdLoadedMatch = false;
            if (mediaFilter.psdLoaded.toString() === '1' && capture.psdLoaded && capture.psdLoaded === 'Y') {
                psdLoadedMatch = true;
            } else if (mediaFilter.psdLoaded.toString() === '0' && !capture.psdLoaded) {
                psdLoadedMatch = true;
            }
            match = psdLoadedMatch;
        }

        if (mediaFilter.keyword && mediaFilter.keyword.length && match) {
            match = matchesKeywords(mediaFilter.keyword, capture.tags, capture.originalFilename, capture.photoCode, capture.styles);
        }

        return match;
    });

    if (mediaFilter.groupBy) {
        var uniq = [...new Set(Object.keys(newData).map((e) => newData[e][mediaFilter.groupBy]))];
        // create the group by items
        var groupedData = uniq.map((group) => {
            var subRows = newData.filter((val) => val[mediaFilter.groupBy] === group);
            return { type: 'group', name: group, subRows, id: group };
        });
        newData = groupedData;
    }

    return newData;
}

export const getMediaFilterCount = (val) => {
    var count = 0;

    if (val.sak > -1) ++count;
    if (val.star > -1) ++count;
    if (val.catalogue?.label?.length) ++count;
    if (val.tags && val.tags.length) ++count;
    if (val.styles && val.styles.length) ++count;
    if (val.status && val.status.taskStatus && val.status.taskStatus.length) ++count;
    if (val.psdLoaded?.length) ++count;
    if (val.colourTag && val.colourTag.length) ++count;
    if (val.showEmptyStyles && val.groupBy && val.groupBy.length) ++count;
    return count;
};

export function filterStyleAssets(mediaItems, mediaFilter) {
    const newData = mediaItems.filter((capture) => {
        var match = true;

        if (mediaFilter.completed && mediaFilter.completed.length === 2 && mediaFilter.completed[0]) {
            const itemDate = moment(capture.completed);
            const startDate = mediaFilter.completed[0];
            const endDate = mediaFilter.completed[1];
            match = itemDate.isBetween(startDate, endDate) ? true : false;
        }

        if (mediaFilter.guides && mediaFilter.guides.length && match) {
            // loop over the tag and see if any match.
            var guideMatch = false;
            for (let i = 0; i < mediaFilter.guides.length; i++) {
                let tag = mediaFilter.guides[i];
                guideMatch = capture.guide?.id === tag ? true : false;
                if (guideMatch) {
                    break;
                }
            }
            match = guideMatch;
        }
        if (mediaFilter.guideCategories && mediaFilter.guideCategories.length && match) {
            // loop over the tag and see if any match.
            guideMatch = false;
            for (let i = 0; i < mediaFilter.guideCategories.length; i++) {
                let tag = mediaFilter.guideCategories[i];
                let arrGuideInfo = tag.split('|');
                let guideId = arrGuideInfo[0];
                let catId = arrGuideInfo[1];

                guideMatch = capture.guide?.id === guideId && capture.guideCategory?.id === catId ? true : false;
                if (guideMatch) {
                    break;
                }
            }
            match = guideMatch;
        }

        if (mediaFilter.tags && mediaFilter.tags.length && match) {
            // loop over the tag and see if any match.
            guideMatch = true;
            for (let i = 0; i < mediaFilter.tags.length; i++) {
                let tag = mediaFilter.tags[i].toLowerCase();
                guideMatch = capture.tags.toLowerCase().indexOf(tag) > -1 ? true : false;
                if (!guideMatch) {
                    guideMatch = false;
                    break;
                }
            }
            match = guideMatch;
        }

        if (mediaFilter.viewstatus && mediaFilter.viewstatus.length && match) {
            logger.log(mediaFilter.viewstatus, capture.viewstatus);
            match = mediaFilter.viewstatus.indexOf(capture.viewstatus) > -1 ? true : false;
        }

        if (mediaFilter.keyword && mediaFilter.keyword.length && match) {
            match = matchesKeywords(mediaFilter.keyword, capture.tags, capture.originalFileName);
        }

        return match;
    });
    return newData;
}

export const getStyleAssetFilterCount = (val) => {
    var count = 0;

    if (val.guideCategories && val.guideCategories.length) ++count;
    if (val.guides && val.guides.length) ++count;
    // if (val.catalogue.label.length) ++count;
    if (val.tags && val.tags.length) ++count;
    if (val.completed && val.completed[0]) ++count;

    return count;
};

export const getTalentAssetFilterCount = (val) => {
    var count = 0;

    // if (val.guideCategories && val.guideCategories.length) ++count;
    if (val.sessionId && val.sessionId.length) ++count;
    // if (val.catalogue.label.length) ++count;
    if (val.star && val.star > -1) ++count;
    if (val.sak && val.sak > -1) ++count;
    if (val.tags && val.tags.length) ++count;
    if (val.completed && val.completed[0]) ++count;

    return count;
};

export const getScanFilterCount = (val) => {
    var count = 0;
    if (val.department && val.department.length) ++count;
    if (val.colour && val.colour.length) ++count;
    if (val.size && val.size.length) ++count;
    if (val.seasons && val.seasons.length) ++count;
    if (val.styles && val.styles.length) ++count;
    if (val.channels && val.channels.length) ++count;
    if (val.sessions && val.sessions.length) ++count;
    if (val.origins && val.origins.length) ++count;
    // if (val.catalogue.label.length) ++count;
    if (val.tags && val.tags.length) ++count;
    if (val.completed && val.completed[0]) ++count;
    if (val.statuses && val.statuses.length) ++count;
    if (val.priorities && val.priorities.length) ++count;
    if (val.location && val.location.length) ++count;
    if (val.scanDateStart) ++count;
    if (val.otbDate?.dateStart?.toString() || val.otbDate?.dateEnd?.toString()) ++count;
    if (val.scannedDate?.dateStart?.toString() || val.scannedDate?.dateEnd?.toString()) ++count;
    if (val.distributionDate?.dateStart?.toString() || val.distributionDate?.dateEnd?.toString()) ++count;
    if (val.createdDate?.dateStart?.toString() || val.createdDate?.dateEnd?.toString()) ++count;
    if (val.colourways && val.colourways.length) ++count;
    if (val.foundInFeed) ++count;
    if (val.captured) ++count;
    if (val.sampleCount?.min != null || val.sampleCount?.max != null) ++count;
    if (val.taskCount?.min != null || val.taskCount?.max != null) ++count;
    return count;
};

export function parseObjectTypes(inputObject) {
    function parseValue(value) {
        if (typeof value === 'object' && value !== null) {
            if ('N' in value) {
                return parseInt(value.N);
            } else if ('S' in value) {
                return value.S;
            } else if ('L' in value) {
                return value.L.map(parseValue);
            } else if ('M' in value) {
                const parsedObject = {};
                for (const key in value.M) {
                    parsedObject[key] = parseValue(value.M[key]);
                }
                return parsedObject;
            }
        }
        return value;
    }

    function parseObject(obj) {
        const parsedObj = {};
        for (const key in obj) {
            parsedObj[key] = parseValue(obj[key]);
        }
        return parsedObj;
    }

    const parsedObject = { ...inputObject };

    for (const key in inputObject) {
        if (key === 'Items') {
            parsedObject[key] = inputObject[key].map((item) => parseObject(item));
        } else {
            parsedObject[key] = parseValue(inputObject[key]);
        }
    }

    return parsedObject;
}

export function bytesToMB(bytes) {
    if (bytes === 0) return '0 MB';

    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
}

export function getOfn(str) {
    var ofn = '';
    const arr = str.split('/');
    if (arr && arr.length) {
        var ofnArr = arr[arr.length - 1].split('?');
        ofn = ofnArr && ofnArr.length ? ofnArr[0] : '';
    }
    return ofn;
}

export const getColumnColor = (title, key = 'name') => {
    const status = statuss.find((val) => val[key] === title);
    const col = status.colors.primary === 'default' ? 'white' : status.colors.primary;
    return col;
};

export const getScanStatusColor = (status) => {
    var col = 'green';
    switch (status.toLowerCase()) {
        case 'failure': {
            col = 'red';
            break;
        }
        case 'pending': {
            col = 'yellow';
            break;
        }
        case 'a2 record not found': {
            col = 'pink';
            break;
        }
        case 'master record not found': {
            col = 'pink';
            break;
        }
        case 'parsed': {
            col = 'indigo';
            break;
        }
        case 'abondoned': {
            col = 'gray';
            break;
        }
        default: {
            break;
        }
    }

    return col;
};

export const getStyleStatusColor = (status) => {
    var col = 'green';
    if(!status)return col;
    switch (status.toUpperCase()) {
        case SampleStatusTypes.SAMPLE_ACTIVE: {
            col = 'green';
            break;
        }
        case SampleStatusTypes.SAMPLE_READY: {
            col = 'violet';
            break;
        }
        case SampleStatusTypes.SAMPLE_DISTRIBUTED: {
            col = 'blue';
            break;
        }
        case SampleStatusTypes.SAMPLE_ARCHIVED: {
            col = 'gray';
            break;
        }
        default: {
            break;
        }
    }

    return col;
};

export const getClientApprovalStatusColor = (status) => {
    var col = 'dimmed';

    switch (status.toUpperCase()) {
        case StudioClientProdStatusTypes.COMPLETE.value: {
            col = 'green';
            break;
        }
        case StudioClientProdStatusTypes.REVIEW.value: {
            col = 'yellow';
            break;
        }
        case StudioClientProdStatusTypes.DRAFT.value: {
            col = 'gray';
            break;
        }
       
        default: {
            break;
        }
    }

    return col;
};


export const getImageFile = async (url, signal) => {
    return fetch(url, {
        headers: {
            hgio: getToken(),
        },
        method: 'GET',
        signal: signal ?? null,
    })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error('ERROR fetching image', err);
            throw err;
        });
};

export const getGuideInfo = (guideID, catId, guides) => {
    var category;
    const guide = guides.find((guide) => guide.id === guideID);
    if (guide && guide.guideCategories) {
        category = guide.guideCategories.find((cat) => cat.id === catId);
    }
    return { guide: guide, category: category };
};

const OTB_INSTRUCTIONS_ACCESSOR = 'instructions["Web Stock OTB Date"]';

export const resolveSampleAccessorValue = (row, accessor) => {
    if (!accessor) return undefined;
    if (typeof accessor === 'function') return accessor(row);
    if (typeof accessor !== 'string') return row?.[accessor];

    if (accessor.startsWith('instructions[')) {
        const m = accessor.match(/^instructions\[(["'])(.*)\1\]$/);
        if (m) return row?.instructions?.[m[2]];
    }

    if (accessor.includes('.')) return _.get(row, accessor);

    return row?.[accessor];
};

export const getSampleOtbRaw = (sample) => {
    const fromInstructions = resolveSampleAccessorValue(sample, OTB_INSTRUCTIONS_ACCESSOR);
    if (fromInstructions !== undefined && fromInstructions !== null && String(fromInstructions).trim() !== '') {
        return fromInstructions;
    }
    // otbDate (warehouse) or OTB (masters import)
    for (const topLevel of [sample?.otbDate, sample?.OTB]) {
        if (topLevel !== undefined && topLevel !== null && String(topLevel).trim() !== '') {
            return topLevel;
        }
    }
    return null;
};

const OTB_PARSE_FORMATS = [
    'DD-MM-YYYY',
    'D-M-YYYY',
    'DD/MM/YYYY',
    'D/M/YYYY',
    DataFormats.DATE_FORMAT_SHORT,
    DataFormats.DATE_FORMAT_REST,
    DataFormats.DATE_FORMAT_CREATE_PARSE,
    'YYYY-MM-DD-HH-mm-ss',
    moment.ISO_8601,
];

/** Parse OTB strings from instructions (e.g. 16-03-2026) or warehouse otbDate fields. */
export const parseSampleOtbMoment = (raw) => {
    if (raw == null || raw === '') return null;
    const trimmed = String(raw).trim();
    if (!trimmed.length) return null;

    for (let i = 0; i < OTB_PARSE_FORMATS.length; i++) {
        const parsed = moment(trimmed, OTB_PARSE_FORMATS[i], true);
        if (parsed.isValid()) return parsed;
    }

    const loose = moment(trimmed);
    return loose.isValid() ? loose : null;
};

/** True when sample has a non-empty, parseable OTB (instructions or top-level otbDate). */
export const isSampleOtbSet = (sample) => parseSampleOtbMoment(getSampleOtbRaw(sample)) != null;

export const resolveAssocStyleSamples = (assocStylesStr, allStyles) => {
    if (!assocStylesStr || !allStyles?.length) return [];
    return assocStylesStr
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .map((id) => allStyles.find((s) => s.stylenumber === id || s.seasonSort === id || s.targetSort === id))
        .filter(Boolean);
};

/** Group-shot parent with no OTB whose assoc styles all have OTB (e.g. PAGE8SHOT1 + 71398501). */
export const isGroupShotShellWithOnlyOtbChildren = (sample, allStyles) => {
    if (!sample?.assocStyles || isSampleOtbSet(sample)) return false;
    const children = resolveAssocStyleSamples(sample.assocStyles, allStyles);
    return children.length > 0 && children.every((child) => isSampleOtbSet(child));
};

export const buildOtbFilterHelpers = (sampleFilter, allStyles = []) => {
    const hasOtbDateFlag =
        sampleFilter?.hasOtbDate != null && sampleFilter?.hasOtbDate !== '' ? String(sampleFilter.hasOtbDate) : '';
    const dateStart = sampleFilter?.otbDate?.dateStart ? moment(sampleFilter.otbDate.dateStart).toDate() : null;
    const dateEnd = sampleFilter?.otbDate?.dateEnd ? moment(sampleFilter.otbDate.dateEnd).toDate() : null;

    const passesOtbFilter = (sample) => {
        const otbIsSet = isSampleOtbSet(sample);
        const rawOtb = otbIsSet ? getSampleOtbRaw(sample) : null;

        if (hasOtbDateFlag === '1' && !otbIsSet) return false;
        if (hasOtbDateFlag === '0' && otbIsSet) return false;

        if ((dateStart || dateEnd) && hasOtbDateFlag !== '0') {
            if (!otbIsSet) return false;

            const parsedOtb = parseSampleOtbMoment(rawOtb).toDate();
            let start = dateStart;
            let end = dateEnd;
            if (dateStart && dateEnd && dateStart.toDateString() === dateEnd.toDateString()) {
                end = new Date(dateEnd);
                end.setHours(23, 59, 59, 999);
            }

            if (start && parsedOtb < start) return false;
            if (end && parsedOtb > end) return false;
        }
        if (hasOtbDateFlag === '0' && isGroupShotShellWithOnlyOtbChildren(sample, allStyles)) {
            return false;
        }
        return true;
    };
    const treeNodePasses = (item) => {
        const nested = item?.subRows?.length ? item.subRows : item?.children?.length ? item.children : null;
        if (!nested?.length) {
            return passesOtbFilter(item);
        }
        return nested.some((child) => treeNodePasses(child));
    };

    const applyOtbPresenceToDisplayData = (items) => {
        if (hasOtbDateFlag !== '0' && hasOtbDateFlag !== '1') return items;
        if (!items?.length) return items;

        const filterTree = (list) =>
            list
                .map((item) => {
                    const next = { ...item };
                    if (next.subRows?.length) next.subRows = filterTree(next.subRows);
                    if (next.children?.length) next.children = filterTree(next.children);
                    return next;
                })
                .filter((item) => treeNodePasses(item));

        return filterTree(items);
    };

    return { passesOtbFilter, applyOtbPresenceToDisplayData, hasOtbDateFlag, treeNodePasses };
};

/**
 * Re-includes assoc parents when any filtered child is in their assocStyles.
 * Parents are not required to pass OTB. Children are optional via filterChildrenWithOtb.
 */
export const appendAssocParentsForFilteredChildren = (data, allStyles, options = {}) => {
    const { passesOtbFilter = null, filterChildrenWithOtb = false } = options;
    if (!data?.length || !allStyles?.length) return data || [];

    const filteredStyleNumbers = data.map((item) => item.stylenumber);
    const existingIds = new Set(data.map((item) => item.id));
    const originalFilteredIds = new Set(existingIds);
    const parentStyles = [];
    const childrenOfFiltered = [];

    allStyles.forEach((item) => {
        if (!item.assocStyles || existingIds.has(item.id)) return;
        const assocStyleNumbers = item.assocStyles.split(',').map((style) => style.trim());
        if (!assocStyleNumbers.some((assocStyle) => filteredStyleNumbers.includes(assocStyle))) return;
        existingIds.add(item.id);
        parentStyles.push({
            ...item,
            type: 'group',
            name: item.seasonSort,
        });
    });

    data.forEach((filteredItem) => {
        if (!filteredItem.assocStyles) return;
        const assocStyleNumbers = filteredItem.assocStyles.split(',').map((style) => style.trim());
        assocStyleNumbers.forEach((assocStyle) => {
            const childStyle = allStyles.find(
                (style) => style.stylenumber === assocStyle || style.seasonSort === assocStyle || style.targetSort === assocStyle,
            );
            if (!childStyle || existingIds.has(childStyle.id)) return;
            if (filterChildrenWithOtb && passesOtbFilter && !passesOtbFilter(childStyle)) return;
            existingIds.add(childStyle.id);
            childrenOfFiltered.push({
                ...childStyle,
                parentId: filteredItem.stylenumber,
                isChildOfFiltered: !originalFilteredIds.has(childStyle.id),
            });
        });
    });

    return data.concat(parentStyles, childrenOfFiltered);
};

export const filterSamples = (samples, sampleFilter, columns = []) => {
    const list = samples || [];
    if (!list.length) return [];

    const { passesOtbFilter } = buildOtbFilterHelpers(sampleFilter);

    // Build Sets once for O(1) membership checks.
    const brandsSet = sampleFilter?.brands?.length ? new Set(sampleFilter.brands) : null;
    const categoriesSet = sampleFilter?.categories?.length ? new Set(sampleFilter.categories) : null;
    const departmentsSet = sampleFilter?.departments?.length ? new Set(sampleFilter.departments) : null;
    const deptsSet = sampleFilter?.depts?.length ? new Set(sampleFilter.depts) : null;
    const channelsSet = sampleFilter?.channels?.length ? new Set(sampleFilter.channels) : null;
    const statusesSet = sampleFilter?.statuses?.length ? new Set(sampleFilter.statuses) : null;
    const seasonsSet = sampleFilter?.seasons?.length ? new Set(sampleFilter.seasons) : null;
    const styleCodesSet = sampleFilter?.stylesCodes?.length ? new Set(sampleFilter.stylesCodes) : null;
    const sessionsSet = sampleFilter?.sessions?.length ? new Set(sampleFilter.sessions) : null;
    const issuersSet = sampleFilter?.issuers?.length ? new Set(sampleFilter.issuers) : null;
    const talentsSet = sampleFilter?.talents?.length ? new Set(sampleFilter.talents) : null;

    // Pre-parse flag filters once.
    const liveDataInt = sampleFilter?.liveData?.length ? parseInt(sampleFilter.liveData) : null;
    const outfittingInt = sampleFilter?.outfittingFlag?.length ? parseInt(sampleFilter.outfittingFlag) : null;
    const multiShotInt = sampleFilter?.multiShotFlag?.length ? parseInt(sampleFilter.multiShotFlag) : null;

    const completedStart = sampleFilter?.completed?.dateStart ? sampleFilter.completed.dateStart : null;
    const completedEnd = sampleFilter?.completed?.dateEnd ? sampleFilter.completed.dateEnd : null;
    const completedActive = !!(completedStart || completedEnd);

    const distStart = sampleFilter?.distribution?.dateStart ? sampleFilter.distribution.dateStart : null;
    const distEnd = sampleFilter?.distribution?.dateEnd ? sampleFilter.distribution.dateEnd : null;
    const distActive = !!(distStart || distEnd);

    const status = sampleFilter?.status || {};
    const statusKeys = Object.keys(status);

    // Keyword and accessor handling.
    const keywordLower = sampleFilter?.keyword?.length ? sampleFilter.keyword.toLowerCase() : '';
    const keywordTerms = keywordLower ? keywordLower.split(' ').filter(Boolean) : [];
    const columnAccessors = columns?.length ? columns.map((c) => c.accessor).filter(Boolean) : [];

    const data = list.filter((sample) => {
        if (brandsSet && !brandsSet.has(sample.facet_brand)) return false;
        if (categoriesSet && !categoriesSet.has(sample.metadata_toplevelcategory)) return false;
        if (departmentsSet && !departmentsSet.has(sample.departmentStr)) return false;
        if (deptsSet && !deptsSet.has(sample.dept)) return false;

        if (channelsSet) {
            const ch = sample.channels || [];
            if (!ch.length) return false;
            if (!ch.some((c) => channelsSet.has(c))) return false;
        }

        if (statusesSet && !statusesSet.has(sample.status)) return false;
        if (seasonsSet && !seasonsSet.has(sample.seasonCode)) return false;
        if (styleCodesSet && !styleCodesSet.has(sample.styleNumber)) return false;
        if (sessionsSet && !sessionsSet.has(sample.sessionCode)) return false;
        if (issuersSet && !issuersSet.has(sample.issuer)) return false;
        if (talentsSet && !sample.talentCodes?.split(',').some((code) => talentsSet.has(code.trim()))) return false;

        if (liveDataInt === 1) {
            if (!sample.liveData?.files?.length) return false;
        } else if (liveDataInt === 0) {
            if (sample.liveData?.files?.length) return false;
        }

        if (outfittingInt === 1) {
            if (!sample.outfittingFlag?.length) return false;
        } else if (outfittingInt === 0) {
            if (sample.outfittingFlag?.length) return false;
        }

        if (multiShotInt === 1) {
            if (!sample.multiShotFlag?.length) return false;
        } else if (multiShotInt === 0) {
            if (sample.multiShotFlag?.length) return false;
        }

        if (!passesOtbFilter(sample)) return false;

        if (completedActive) {
            const completedDate = moment(sample.completed, 'YYYY-MM-DD-HH-mm-ss-SSS').toDate();
            if (completedStart && completedDate < completedStart) return false;
            if (completedEnd && completedDate > completedEnd) return false;
        }

        if (distActive) {
            const distDate = moment(sample.distributionDate, 'YYYY-MM-DD-HH-mm-ss-SSS').toDate();
            if (distStart && distDate < distStart) return false;
            if (distEnd && distDate > distEnd) return false;
        }

        // the status key values represent
        // '' = matches all
        // 1 matches values of the sample key value > 0
        // 0 matches values of the sample key value < 1            
        for (let i = 0; i < statusKeys.length; i++) {
            const key = statusKeys[i];
            const filterVal = status[key];
            
            // Skip if no filter is set (empty string, null, undefined)
            if (!filterVal && filterVal !== 0) continue; // Allow 0 but skip '', null, undefined
            
            const filterInt = parseInt(filterVal);
            
            // Skip if filter is not a valid number
            if (Number.isNaN(filterInt)) continue;
            
            const sampleInt = parseInt(sample[key]);
            const sampleValue = Number.isNaN(sampleInt) ? 0 : sampleInt; // Treat missing as 0
            
            if (filterInt === 1) {
                // Filter = 1: only show items WITH values (> 0)
                if (sampleValue <= 0) return false;
            } else if (filterInt === 0) {
                // Filter = 0: only show items WITHOUT values (< 1, i.e., 0 or missing)
                if (sampleValue >= 1) return false;
            }
            // Empty string matches all (already handled by continue above)
        }

        if (keywordTerms.length) {
            const allValues = [
                sample.ssPart, sample.styleNumber, sample.seasonCode,
                sample.sessionCode, sample.ssSort, sample.name,
                sample.desc, sample.photoCode,
            ];

            if (columnAccessors.length) {
                for (let i = 0; i < columnAccessors.length; i++) {
                    allValues.push(resolveSampleAccessorValue(sample, columnAccessors[i]));
                }
            }

            const lowerValues = allValues.map((v) => (v ?? '').toString().toLowerCase());
            const anyTermMatch = keywordTerms.some((term) => lowerValues.some((val) => val.includes(term)));
            if (!anyTermMatch) return false;
        }

        return true;
    });

    if (sampleFilter?.groupBy) {
        const groupBy = sampleFilter.groupBy;

        if (groupBy === 'timeForecast') {
            // Group by array values - need special handling
            const groupMap = new Map();
            data.forEach((item) => {
                const key = JSON.stringify(item.timeForecast);
                if (!groupMap.has(key)) groupMap.set(key, []);
                groupMap.get(key).push(item);
            });

            const groupedData = [];
            groupMap.forEach((subRows, key) => {
                const timeArray = JSON.parse(key);
                const displayName = timeArray.map((ts) => new Date(ts * 1000).toLocaleString()).join(' - ');
                groupedData.push({ type: 'group', name: displayName, subRows, id: key });
            });
            return groupedData;
        }

        // Standard grouping: O(n) via Map (avoid uniq + repeated filter()).
        const groupMap = new Map();
        data.forEach((item) => {
            const group = item[groupBy];
            if (!groupMap.has(group)) groupMap.set(group, []);
            groupMap.get(group).push(item);
        });
        return Array.from(groupMap.entries()).map(([group, subRows]) => ({ type: 'group', name: group, subRows, id: group }));
    }

    return data;
};

export const sampleAlphanumericFilter = (row1, row2, columnName, isTableSort = true) => {
    var rowOneColumn = isTableSort ? row1.values[columnName] : row1[columnName];
    var rowTwoColumn = isTableSort ? row2.values[columnName] : row2[columnName];

    if (columnName.toLowerCase().indexOf('instructions') > -1) {
        let regex = /\["([^"]+)"\]/;
        let match = columnName.match(regex);

        if (match) {
            let key = match[1];
            try {
                rowOneColumn = isTableSort ? row1.original.instructions[key] : row1.instructions[key];
                rowTwoColumn = isTableSort ? row2.original.instructions[key] : row2.instructions[key];
            } catch (e) {
                rowOneColumn = '';
                rowTwoColumn = '';
            }
        } else {
            console.log('No match found.');
        }
    }

    if (isTableSort) {
        if (rowOneColumn == null || rowOneColumn === '') {
            rowOneColumn = row1.original?.[columnName];
        }
        if (rowTwoColumn == null || rowTwoColumn === '') {
            rowTwoColumn = row2.original?.[columnName];
        }
    }

    const one = rowOneColumn == null || rowOneColumn === '' ? '' : rowOneColumn.toString().trim();
    const two = rowTwoColumn == null || rowTwoColumn === '' ? '' : rowTwoColumn.toString().trim();

    if (!one && !two) return 0;
    if (!one) return 1;
    if (!two) return -1;

    const oneNum = Number(one);
    const twoNum = Number(two);
    if (!Number.isNaN(oneNum) && !Number.isNaN(twoNum) && `${oneNum}` === one && `${twoNum}` === two) {
        return oneNum - twoNum;
    }

    return one.localeCompare(two, undefined, { sensitivity: 'base', numeric: true });
};


export const parentIdFilter = (row1, row2, columnName, isTableSort = true) => {
    var items = sampleAlphanumericFilter(row1, row2, columnName, isTableSort);
    return items;
};

/**
 * Table filter that puts items with children at the top of the data
 * Items with more children appear first, followed by items without children
 * @param {Object} row1 - First row to compare
 * @param {Object} row2 - Second row to compare
 * @param {string} columnName - Column name (unused for this filter)
 * @param {boolean} isTableSort - Whether this is for table sorting (default: true)
 * @returns {number} Sort comparison result
 */
export const childrenPriorityFilter = (row1, row2, columnName, isTableSort = true) => {
    const item1 = isTableSort ? row1.original : row1;
    const item2 = isTableSort ? row2.original : row2;
    
    const children1Count = item1.children?.length || 0;
    const children2Count = item2.children?.length || 0;
    
    // Items with more children come first (descending order)
    if (children1Count !== children2Count) {
        return children2Count - children1Count;
    }
    
    // If both have same number of children, fall back to alphanumeric sorting
    return sampleAlphanumericFilter(row1, row2, columnName, isTableSort);
};


// export const dateSortFilter = (row1, row2, columnName, isTableSort = true) => {
//     var rowOneColumn = isTableSort ? row1.values[columnName] : row1[columnName];
//     var rowTwoColumn = isTableSort ? row2.values[columnName] : row2[columnName];

//     const val1 = moment(rowOneColumn, 'YYYY-MM-DD-hh-mm-ss').format();
//     const val2 = moment(rowTwoColumn, 'YYYY-MM-DD-hh-mm-ss').format();

//     logger.log(val1, val2, 'val1, val2');

//     return new Date(val2) - new Date(val1);
// };

export const dateSortFilter = (row1, row2, columnName, isTableSort = true) => {
    var rowOneColumn = isTableSort ? row1.values[columnName] : row1[columnName];
    var rowTwoColumn = isTableSort ? row2.values[columnName] : row2[columnName];

    // Handle empty/null/undefined values
    if (!rowOneColumn || rowOneColumn === '') {
        return !rowTwoColumn || rowTwoColumn === '' ? 0 : 1; // Empty sorts last
    }
    if (!rowTwoColumn || rowTwoColumn === '') {
        return -1; // Empty sorts last
    }

    // Parse dates and check validity
    const moment1 = moment(rowOneColumn, 'YYYY-MM-DD-hh-mm-ss');
    const moment2 = moment(rowTwoColumn, 'YYYY-MM-DD-hh-mm-ss');

    // Treat invalid dates as empty (sort them last)
    const isValid1 = moment1.isValid();
    const isValid2 = moment2.isValid();

    if (!isValid1 && !isValid2) return 0; // Both invalid, equal
    if (!isValid1) return 1; // Invalid sorts after valid
    if (!isValid2) return -1; // Invalid sorts after valid

    // Both valid, compare normally
    const date1 = moment1.toDate();
    const date2 = moment2.toDate();
    
    return date2 - date1; // Descending order (newest first)
};

// export const headerDateSort = (a, b, key) => {
//     var val1 = 0;
//     var val2 = 0;

//     if (a.values[key] && a.values[key].length) {
//         val1 = moment(a.values[key], DataFormats.DATE_FORMAT_CREATE_PARSE).format();
//     }
//     if (b.values[key] && b.values[key].length) {
//         val2 = moment(b.values[key], DataFormats.DATE_FORMAT_CREATE_PARSE).format();
//     }

//     return new Date(val1) - new Date(val2);
// };

export const headerDateSort = (rowA, rowB, columnId) => {
    const a = rowA.values[columnId];
    const b = rowB.values[columnId];

    // Handle cases where values don't exist or are empty
    if (!a || !a.length) {
        return b && b.length ? 1 : 0; // Put empty values at the end
    }
    if (!b || !b.length) {
        return -1; // Put empty values at the end
    }

    // Parse dates directly with moment and compare timestamps
    const dateA = moment(a, DataFormats.DATE_FORMAT_CREATE_PARSE);
    const dateB = moment(b, DataFormats.DATE_FORMAT_CREATE_PARSE);

    // Check if both dates are valid
    if (!dateA.isValid()) return dateB.isValid() ? 1 : 0;
    if (!dateB.isValid()) return -1;

    // Compare using moment's valueOf() which gives timestamps
    return dateA.valueOf() - dateB.valueOf();
};

export const getStatusColor = (status) => {
    logger.log(status, " ------------")
    if (!status) return;
    var color = 'red';
    switch (status.toUpperCase()) {
        case 'SUCCEEDED': 
        case 'ACTIVE':
        case 'CAPTURED':
        case 'SUCCESS': {
            color = 'teal';
            break;
        }
        case 'SESSION_SCHEDULED':
        case 'PENDING': {
            color = 'orange';
            break;
        }
        case 'ARCHIVE': {
            color = 'gray';
            break;
        }
        case 'NOT_YET_ACTIONED':
        case 'INACTIVE': {
            color = 'gray';
            break;
        }
        case 'DISTRIBUTED': {
            color = 'blue';
            break;
        }
        case 'POST_PRODUCTION': {
            color = 'indigo';
            break;
        }
         case 'SAMPLE_ACQUIRED': {
            color = 'grape';
            break;
        }

        // need colors for POST_PRODUCTION, CAPTURED, SESSION_SCHEDULED, SAMPLE_ACQUIRED, NOT_YET_ACTIONED
        default: {
            break;
        }
    }

    return color;
};

export const createMembers = (aggData, allowUnassign = false) => {
    var newMembers = [];

    if (aggData && aggData.productionTeam) {
        const productionTeam = aggData.productionTeam.map((val, idx) => {
            // const themeCol = colorThemes[idx + 2];
            const userInitials = getUserInitials(val.name.split(' ')[0], val.name.split(' ')[1]);
            logger.log(userInitials);
            const themeCol = getUserAvatarColour(userInitials);
            return { name: val.name, color: themeCol, team: 'Production', email: val.email };
        });
        const postTeam = aggData.postProductionTeam.map((val, idx) => {
            // const themeCol = colorThemes[idx + 2];
            const userInitials = getUserInitials(val.name.split(' ')[0], val.name.split(' ')[1]);
            const themeCol = getUserAvatarColour(userInitials);
            return { name: val.name, color: themeCol, team: 'Post Production', email: val.email };
        });
        var tmpMembers = postTeam.concat(productionTeam);
        tmpMembers.sort((a, b) => a.name.localeCompare(b.name));

        newMembers = tmpMembers.map((val, idx) => {
            return { value: val.name, label: val.name, group: val.team, color: val.color, email: val.email };
        });

        if (allowUnassign) {
            newMembers = [{ value: status_unassigned.value, label: 'Unassign', group: 'Unassign', color: '', email: '' }, ...newMembers];
        }
    }

    return newMembers;
};

export const useWindowSize = () => {
    let [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
};

export const getTalentFromSession = (session, talents) => {
    var tmpTalents = [];
    var talentMap = {};
    if (session && session.samples) {
        var newData = [];

        newData = session.samples.map((val) => {
            if (val.talentCodes && val.talentCodes.length) {
                val.talentCodes.split(',').forEach((talentCode) => {
                    const talent = talents.find((item) => item.code.trim() === talentCode);
                    if (talent && !talentMap[talent.code]) {
                        talentMap[talent.code] = talent;
                        tmpTalents.push(talent);
                    }
                });
            }
        });
    }
    return tmpTalents;
};

export const DateRanges = {
    TODAY: 0,
    YESTERDAY: 1,
    THIS_WEEK: 2,
    LAST_WEEK: 3,
    LAST_30_DAYS: 4,
    LAST_90_DAYS: 5,
    LAST_12_MONTHS: 6,
    CUSTOM: 7,
};

export const DateSelections = [
    { label: 'Today', value: DateRanges.TODAY },
    // { label: 'Yesterday', value: DateRanges.YESTERDAY },
    { label: 'This week', value: DateRanges.THIS_WEEK },
    { label: 'Last week', value: DateRanges.LAST_WEEK },
    // { label: 'Last 30 days', value: DateRanges.LAST_30_DAYS },
    // { label: 'Last 90 days', value: DateRanges.LAST_90_DAYS },
    // { label: 'Last 12 months', value: DateRanges.LAST_12_MONTHS },
    { label: 'Custom', value: DateRanges.CUSTOM },
];

export const getDateRanges = (item) => {
    const now = new Date();
    let startDate = new Date(now);
    let endDate;

    switch (item.value) {
        case DateRanges.TODAY:
            startDate = new Date(now);
            startDate.setHours(0, 0, 0, 0); // Set time to the beginning of the day (00:00:00)
            endDate = new Date(now);
            endDate.setHours(23, 59, 59, 999);
            break;
        case DateRanges.YESTERDAY:
            startDate.setDate(now.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now);
            endDate.setDate(now.getDate() - 1);
            endDate.setHours(23, 59, 59, 999);
            break;
        case DateRanges.THIS_WEEK:
            startDate.setDate(now.getDate() - now.getDay());
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now);
            break;
        case DateRanges.LAST_WEEK:
            startDate.setDate(now.getDate() - now.getDay() - 7);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now);
            endDate.setDate(now.getDate() - now.getDay() - 1);
            endDate.setHours(23, 59, 59, 999);
            break;
        case DateRanges.LAST_30_DAYS:
            startDate.setDate(now.getDate() - 30);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now);
            break;
        case DateRanges.LAST_90_DAYS:
            startDate.setDate(now.getDate() - 90);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now);
            break;
        case DateRanges.LAST_12_MONTHS:
            startDate.setMonth(now.getMonth() - 12);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(now);
            break;
        default:
            startDate = null;
            endDate = null;
    }

    return { startDate, endDate };
};

/**
 * Helper to parse the error response code and determine whether we need to throw or not.
 * At the moment we just look at whether a repsonse has been aborted.
 * @param {*} err
 * @returns
 */
export const doThrowOnResponse = (err) => {
    if (err && err.code === ResponseCodes.ABORT_ERROR) {
        console.warn('doThrowOnResponse: ', err);
        return false;
    } else {
        console.error('doThrowOnResponse: ', err);
        return true;
    }
};

export const formatCompanySpecificData = (companyCode, data, formatType = 'DEFAULT') => {
    const companyFormats = CompanyFormats[companyCode];
    var result;

    if (companyFormats) {
        switch (formatType) {
            case CompanyFormatFields.ASSET_DISTRIBUTION_NAME: {
                const { style, shot } = data;
                result = companyFormats[formatType]({ style, shot });
                break;
            }
            default: {
                break;
            }
        }
    }

    if (!companyFormats || !companyFormats[formatType]) {
        throw new Error(`No format defined for company: ${companyCode} (${formatType})`);
    }
    return result;
};

export const getCompany = (aggData) => {
    var company;
    if (aggData && aggData.clients && aggData.clients.length) {
        company = aggData.clients[0];
    }
    return company;
};

export const formatDateFilterFields = (val) => {
    // Use moment to convert the date to UTC
    const utcMoment = moment(val);

    // Store the UTC date as an ISO string
    const utcDateString = utcMoment.toISOString();

    return utcDateString;
};

export const purgeFilters = (filters) => {
    const now = Date.now();
    const maxAge = AppConstants.FILTER_MAX_AGE_IN_DAYS * 24 * 60 * 60 * 1000;
    const newFilters = Object.entries(filters)
        .filter(([_, filter]) => {
            logger.log(filter.timestamp, now, maxAge);
            return filter.timestamp && now - filter.timestamp < maxAge;
        })
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});

    return newFilters;
};

export const getTotalDuration = (forecast) => {
    if (forecast && forecast[0] && forecast[1]) {
        var displayString = moment.duration(forecast[1] - forecast[0]).hours() + 'h ' + moment.duration(forecast[1] - forecast[0]).minutes() + 'm';
        var progress = ((forecast[1] - forecast[0]) / (24 * 60 * 60 * 1000)) * 100;
        if (progress < 0) {
            progress = 0;
        }
        if (progress > 100) {
            progress = 100;
        }
        return { display: displayString, duration: forecast[1] - forecast[0], progress: progress };
    } else {
        return { display: '0h 0m', duration: 0, progress: 0 };
    }
};


export function isJsonString(str) {
    if (typeof str !== 'string') return false;
    
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
}


// write a function to convert rgb to hex
export const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
        const hex = parseInt(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('');
}

export const labToRgb = (l, a, b) => {
    // Convert LAB to XYZ
    let y = (l + 16) / 116;
    let x = a / 500 + y;
    let z = y - b / 200;

    x = 0.95047 * ((x * x * x > 0.008856) ? x * x * x : (x - 16/116) / 7.787);
    y = 1.00000 * ((y * y * y > 0.008856) ? y * y * y : (y - 16/116) / 7.787);
    z = 1.08883 * ((z * z * z > 0.008856) ? z * z * z : (z - 16/116) / 7.787);

    // Convert XYZ to RGB
    let r = x *  3.2406 + y * -1.5372 + z * -0.4986;
    let g = x * -0.9689 + y *  1.8758 + z *  0.0415;
    let b_rgb = x *  0.0557 + y * -0.2040 + z *  1.0570;

    r = (r > 0.0031308) ? (1.055 * Math.pow(r, 1/2.4) - 0.055) : 12.92 * r;
    g = (g > 0.0031308) ? (1.055 * Math.pow(g, 1/2.4) - 0.055) : 12.92 * g;
    b_rgb = (b_rgb > 0.0031308) ? (1.055 * Math.pow(b_rgb, 1/2.4) - 0.055) : 12.92 * b_rgb;

    return [
        Math.max(0, Math.min(1, r)) * 255,
        Math.max(0, Math.min(1, g)) * 255,
        Math.max(0, Math.min(1, b_rgb)) * 255
    ];
};


export const generateColKeys = (columns, data) => {
    // Extract the accessor keys from the columns array
    const columnKeys = columns.map(column => 
      column.accessorKey || 
      (typeof column.accessor === 'string' 
        ? column.accessor 
        : column.accessor?.key)
    ).filter(Boolean);
  
    return columnKeys;
  }
  

  export const organiseGroupShots = (styles, availableStyles = [], options = {}) => {
    const { passesOtbFilter = null } = options;
    const childPassesFilter = (child) => !passesOtbFilter || passesOtbFilter(child);

    // we need to add a parentId to styles if they appear as items in another item's assocStyles array
    // Create a map to track which items should have which parentId
    // a style can have more than one parentId - store as arrays
    if(!styles) return [];
    const parentIdMap = {};
    var tmpData = [...styles];
    
    // Use availableStyles if provided, otherwise fall back to styles for finding associations
    const searchData = availableStyles.length > 0 ? availableStyles : styles;
    
    if (searchData) {
        searchData?.forEach((item) => {
            if (item.assocStyles) {
                // This item is a parent - it groups other styles
                // Find all the associated styles and add their parentId to this item's seasonSort
                const associatedStyleIds = item.assocStyles.split(',');
                associatedStyleIds.forEach((assocStyleId) => {
                    const trimmedId = assocStyleId.trim();
                    if (!parentIdMap[trimmedId]) {
                        parentIdMap[trimmedId] = [];
                    }
                    // Only add if not already present
                    if (!parentIdMap[trimmedId].includes(item.stylenumber)) {
                        parentIdMap[trimmedId].push(item.stylenumber);
                    }
                });
            }
        });
    }
    
    // Create expanded data array to handle multiple parent relationships
    let expandedData = [];
    const usedIds = new Set(); // Track used IDs to prevent duplicates
    
    tmpData.forEach((item) => {
        const parentIds = parentIdMap[item.stylenumber];
        if (parentIds && parentIds.length > 0) {
            // Create a copy for each parent relationship
            parentIds.forEach((parentId) => {
                const uniqueId = `${item.id}-${parentId}`;
                // Only add if this ID hasn't been used yet
                if (!usedIds.has(uniqueId)) {
                    usedIds.add(uniqueId);
                    expandedData.push({ 
                        ...item, 
                        parentId: parentId,
                        id: uniqueId
                    });
                }
            });
        } else {
            // No parent relationship - use original ID if not already used
            if (!usedIds.has(item.id)) {
                usedIds.add(item.id);
                expandedData.push(item);
            }
        }
    });

    // for the parents add a new subrow to show the assoc styles
    expandedData = expandedData.map((item) => {
        // Check if this item is a parent (has children with this item's seasonSort as parentId)
        const children = expandedData.filter((childItem) => childItem.parentId === item.stylenumber).filter(childPassesFilter);

        // If no children found in expandedData but availableStyles is provided, 
        // look for child styles in availableStyles that might not be in the original styles array
        let additionalChildren = [];
        if (children.length === 0 && availableStyles.length > 0 && item.assocStyles) {
            const associatedStyleIds = item.assocStyles.split(',');
            additionalChildren = associatedStyleIds
                .map((assocStyleId) => {
                    const trimmedId = assocStyleId.trim();
                    // Find the child style in availableStyles
                    const childStyle = availableStyles.find((style) => 
                        style.stylenumber === trimmedId || 
                        style.seasonSort === trimmedId || 
                        style.targetSort === trimmedId
                    );
                    if (childStyle) {
                        const uniqueChildId = `${childStyle.id}-${item.stylenumber}`;
                        // Only add if this child ID hasn't been used yet
                        if (!usedIds.has(uniqueChildId)) {
                            usedIds.add(uniqueChildId);
                            return {
                                ...childStyle,
                                parentId: item.stylenumber,
                                id: uniqueChildId
                            };
                        }
                    }
                    return null;
                })
                .filter(Boolean)
                .filter(childPassesFilter);
        }
        
        const allChildren = [...children, ...additionalChildren].filter(childPassesFilter);
        // logger.log('allChildren', allChildren);
        // logger.log('expandedData', expandedData);
        if (allChildren.length > 0) {
            return {
                ...item,
                children: allChildren.map((child, idx) => {
                    // Ensure child has a unique ID
                    let childId = child.id;
                    if (!childId || usedIds.has(childId)) {
                        // Generate a unique ID if none exists or if it's already used
                        childId = `${child.id || child.stylenumber || 'child'}-${item.stylenumber}-${idx}`;
                        let counter = 0;
                        while (usedIds.has(childId)) {
                            counter++;
                            childId = `${child.id || child.stylenumber || 'child'}-${item.stylenumber}-${idx}-${counter}`;
                        }
                        usedIds.add(childId);
                    }
                    // Create a new object instead of modifying the existing one
                    return {
                        ...child,
                        id: childId
                    };
                }),
            };
        }
        return item;
    });

    // Final deduplication pass to ensure no duplicate IDs remain
    const finalData = [];
    const finalUsedIds = new Set();
    
    expandedData.forEach((item) => {
        // Reset parentId and clean up ID
        let cleanId = item.id;
        let cleanParentId = item.parentId;
        
        if (item.parentId) {
            // Remove the parent suffix from ID
            cleanId = item.id.replace(/-[A-F0-9]{6}$/i, '');
            cleanParentId = '';
        }
        
        // Ensure the cleaned ID is unique
        if (!finalUsedIds.has(cleanId)) {
            finalUsedIds.add(cleanId);
            // Create a new object instead of modifying the existing one
            finalData.push({
                ...item,
                id: cleanId,
                parentId: cleanParentId
            });
        }
    });

    // Validate that all IDs are unique
    const validation = validateUniqueIds(finalData);
    if (!validation.isValid) {
        logger.warn('Duplicate IDs found in organiseGroupShots:', validation.duplicates);
        // Remove duplicates using 'first' strategy to maintain data integrity
        const deduplicatedData = removeDuplicateIds(finalData);
        logger.log('Removed duplicates, final count:', deduplicatedData.length);
        finalData = deduplicatedData;
    }

    logger.log('finalData', finalData, parentIdMap);
    // add the items with children to the start of the data.
    finalData.sort((a, b) => {
        return (b.children?.length || 0) - (a.children?.length || 0);
    });

    return finalData;
};

/**
 * Validates that all items in an array have unique IDs
 * @param {Array} items - Array of items to validate
 * @param {string} idField - The field name that contains the ID (default: 'id')
 * @returns {Object} - Object containing validation result and duplicate information
 */
export const validateUniqueIds = (items, idField = 'id') => {
    const idCounts = {};
    const duplicates = [];
    
    items.forEach((item, index) => {
        const id = item[idField];
        if (id !== undefined && id !== null) {
            if (idCounts[id]) {
                idCounts[id].push(index);
                if (idCounts[id].length === 2) {
                    // First duplicate found
                    duplicates.push({
                        id: id,
                        indices: [idCounts[id][0], index]
                    });
                } else {
                    // Additional duplicate
                    duplicates[duplicates.length - 1].indices.push(index);
                }
            } else {
                idCounts[id] = [index];
            }
        }
    });
    
    return {
        isValid: duplicates.length === 0,
        duplicates: duplicates,
        totalItems: items.length,
        uniqueIds: Object.keys(idCounts).length
    };
};

/**
 * Removes duplicate items based on ID field
 * @param {Array} items - Array of items to deduplicate
 * @param {string} idField - The field name that contains the ID (default: 'id')
 * @param {string} strategy - Strategy for handling duplicates: 'first' (keep first occurrence), 'last' (keep last occurrence)
 * @returns {Array} - Array with duplicates removed
 */
export const removeDuplicateIds = (items, idField = 'id', strategy = 'first') => {
    const seen = new Set();
    const result = [];
    
    items.forEach((item) => {
        const id = item[idField];
        if (id !== undefined && id !== null) {
            if (!seen.has(id)) {
                seen.add(id);
                result.push(item);
            } else if (strategy === 'last') {
                // Replace the existing item with the current one
                const existingIndex = result.findIndex(existingItem => existingItem[idField] === id);
                if (existingIndex !== -1) {
                    result[existingIndex] = item;
                }
            }
            // For 'first' strategy, we don't add duplicates (already handled above)
        } else {
            // Items without IDs are always included
            result.push(item);
        }
    });
    
    return result;
};


export const hasValidSubRows = (row) => {
    if (row?.subRows?.length === 1) {
        var id = row.subRows[0].original && row.subRows[0].original.id ? row.subRows[0].original.id : row.subRows[0].id;
        if(id.includes('empty-')) {
            return false;
        }
    }
    return true;
}


/**
 * Priority type for an OTB date, based on how many days away it is from today.
 * Accepts a raw OTB string, Date or moment; a missing or unparseable OTB is ERROR.
 */
export const getPriorityForOtb = (otb) => {
    const otbDate = moment.isMoment(otb) || otb instanceof Date ? moment(otb) : parseSampleOtbMoment(otb);
    if (!otbDate || !otbDate.isValid()) return PriorityTypesMap.ERROR;
    const daysUntilOtb = otbDate.clone().startOf('day').diff(moment().startOf('day'), 'days');
    return priorityForDaysUntilOtb(daysUntilOtb);
};

/**
 * Priority type for a stored priority value. When the value is missing or no longer
 * a known priority (e.g. the old HIGH/MEDIUM values) it falls back to the OTB date.
 */
export const getItemPriority = (priorityType, otb) => {
    return PriorityTypes.find((p) => p.value === priorityType) ?? getPriorityForOtb(otb);
}

export const getClientLabel = (company, key, defaultValue = '') => {
    if (!company?.code) return defaultValue;
    const labels = CompanyFormats[company.code]?.LABELS ?? CompanyFormats.DEFAULT?.LABELS ?? {};
    return labels[key] ?? defaultValue;
};

export const getCommonPinningStyles = (column) => {
    // react-table v7 doesn't have column pinning - return width only
    if (typeof column?.getIsPinned !== "function") {
      return { width: column?.width };
    }
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "-4px 0 4px -4px gray inset"
        : isFirstRightPinnedColumn
        ? "4px 0 4px -4px gray inset"
        : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? "sticky" : "relative",
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
    };
  };
  